import { Box, Divider } from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'

import createHref from 'lib/createHref'
import { contentMargin } from 'theme/space'
import { Heading } from 'atoms/typography'
import Title from 'components/Title'
import LayoutContainer from 'components/LayoutContainer'
import NavBar, { NavBarProps } from 'components/NavBar'
import WalkMap, { WalkMapContainer, MapStopProps } from 'components/WalkMap'
import ContinueNav, { ContinueButtonType } from 'components/ContinueNav'
import Footer from 'components/Footer'
import Auth from 'components/Auth'
import useSaveProgress, {
  SaveProgressParams,
} from 'lib/hooks/api/useSaveProgress'
import useCompleteWalk from 'lib/hooks/api/useCompleteWalk'

import useIWitnessActivityId from './useIWitnessActivityId'

const baseExitUrl =
  process.env.NEXT_PUBLIC_IWITNESS_ACTIVITIES_PAGE_BASE_URL ?? ''

export interface MapInterstitialProps {
  walkName: string
  stops: MapStopProps[]
  walkSlug: string
  nextStop: number | null
  navBarProps: NavBarProps
  saveProgressParams: SaveProgressParams
}

const MapInterstitial: React.FC<MapInterstitialProps> = ({
  walkName,
  nextStop,
  stops,
  walkSlug,
  navBarProps,
  saveProgressParams,
}) => {
  const { t } = useTranslation()

  useSaveProgress(saveProgressParams)

  const isWalkComplete = nextStop == null

  const activityId = useIWitnessActivityId(walkSlug, isWalkComplete)
  const { success } = useCompleteWalk(walkSlug, isWalkComplete)

  const isLoading = isWalkComplete && success === false && activityId == null

  return (
    <>
      <Title>{walkName}</Title>

      <Auth walkSlug={walkSlug} />

      <NavBar {...navBarProps} />

      <WalkMapContainer>
        <WalkMap
          stops={stops}
          walkSlug={walkSlug}
          initialActiveStop={!isWalkComplete ? nextStop : stops.length - 1}
          carouselWidth={{ base: '100%', sm: '83%' }}
        />
      </WalkMapContainer>

      <LayoutContainer>
        <Box w={{ base: '100%', sm: '83%' }} textAlign="center">
          {isWalkComplete ? (
            <Heading size="lg" mb={contentMargin.lg}>
              {t('completionPage.completion')}
            </Heading>
          ) : null}

          <Divider mb={contentMargin.lg} />
        </Box>

        <ContinueNav
          continueIsDisabled={isLoading}
          continueHref={
            !isWalkComplete
              ? createHref({ walk: walkSlug, stop: stops[nextStop].slug })
              : `${baseExitUrl}${activityId ?? ''}`
          }
          continueText={
            !isWalkComplete
              ? t('button.continueToStop', { stop: nextStop + 1 })
              : t('button.exit')
          }
          continueButtonType={ContinueButtonType.Solid}
          mapHref={createHref({ walk: walkSlug })}
          mapText={t('button.backToWalk')}
        />
      </LayoutContainer>

      <Footer />
    </>
  )
}

export default MapInterstitial
